import { Link } from "react-router-dom";
import { ArrowLeft, Building2, FileText, Scale } from "lucide-react";

export default function LegalCompliancePage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[var(--secondary)] text-white py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/help"
            className="inline-flex items-center gap-2 text-white hover:text-gray-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Help Centre
          </Link>
          <h1 className="text-4xl font-bold">Legal & Compliance</h1>
          <p className="text-gray-200 mt-2">
            Understanding legal requirements and compliance for workspace rentals
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        {/* Disclaimer */}
        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8 rounded-r-lg">
          <div className="flex items-start gap-3">
            <Scale className="w-6 h-6 text-yellow-700 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-yellow-900 mb-2">
                Legal Disclaimer
              </h3>
              <p className="text-sm text-yellow-800">
                The information provided here is for general guidance only and
                does not constitute legal advice. Laws and regulations vary
                significantly by location and change over time. Always consult
                with a qualified attorney or legal professional in your
                jurisdiction before making legal decisions.
              </p>
            </div>
          </div>
        </div>

        {/* For Tenants */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <h2 className="text-3xl font-bold mb-6">For Tenants</h2>

          <div className="space-y-8">
            {/* Lease Agreements */}
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-blue-600" />
                Understanding Your Lease Agreement
              </h3>
              <p className="text-gray-700 mb-3">
                Commercial leases are complex legal documents. Key terms to
                understand:
              </p>

              <div className="space-y-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Lease Types</h4>
                  <ul className="space-y-2 text-sm text-gray-700">
                    <li>
                      <strong>Gross Lease:</strong> Rent includes most operating
                      expenses (taxes, insurance, maintenance)
                    </li>
                    <li>
                      <strong>Net Lease (NNN):</strong> Tenant pays base rent
                      plus property taxes, insurance, and maintenance
                    </li>
                    <li>
                      <strong>Modified Gross:</strong> Hybrid where some
                      expenses are shared between landlord and tenant
                    </li>
                    <li>
                      <strong>Percentage Lease:</strong> Base rent plus
                      percentage of business revenue (common in retail)
                    </li>
                  </ul>
                </div>

                <div>
                  <h4 className="font-semibold mb-2">
                    Critical Lease Provisions
                  </h4>
                  <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                    <li>
                      <strong>Term & Renewal:</strong> Lease duration and options
                      to renew at end of term
                    </li>
                    <li>
                      <strong>Rent Escalation:</strong> How and when rent
                      increases (annual %, CPI-based, etc.)
                    </li>
                    <li>
                      <strong>Use Clause:</strong> Permitted business activities
                      in the space
                    </li>
                    <li>
                      <strong>Exclusivity:</strong> Protection from competing
                      businesses in same property
                    </li>
                    <li>
                      <strong>Assignment & Subletting:</strong> Rights to
                      transfer lease or sublet space
                    </li>
                    <li>
                      <strong>Maintenance Obligations:</strong> Who repairs what
                      (roof, HVAC, plumbing, etc.)
                    </li>
                    <li>
                      <strong>Improvements & Alterations:</strong> What
                      modifications are allowed and who pays
                    </li>
                    <li>
                      <strong>Early Termination:</strong> Conditions and
                      penalties for breaking the lease
                    </li>
                  </ul>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Before You Sign:</strong> Have an attorney review
                    your lease agreement. Commercial leases heavily favor
                    landlords; negotiation is expected and encouraged.
                  </p>
                </div>
              </div>
            </div>

            {/* Business Licenses & Permits */}
            <div>
              <h3 className="text-2xl font-semibold mb-4 flex items-center gap-2">
                <Building2 className="w-6 h-6 text-blue-600" />
                Required Licenses & Permits
              </h3>
              <p className="text-gray-700 mb-3">
                Operating a business from a commercial space requires various
                approvals:
              </p>

              <div className="space-y-3">
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold mb-1">
                    Business License / Operating Permit
                  </h4>
                  <p className="text-sm text-gray-600">
                    Required by most cities/counties to operate a business.
                    Obtained from local government. Renewal typically annual.
                  </p>
                </div>

                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold mb-1">
                    Certificate of Occupancy (CO)
                  </h4>
                  <p className="text-sm text-gray-600">
                    Verifies the building is safe and approved for your intended
                    use. Usually provided by landlord, but confirm it covers your
                    business type.
                  </p>
                </div>

                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold mb-1">Zoning Compliance</h4>
                  <p className="text-sm text-gray-600">
                    Ensures your business type is allowed in that location.
                    Check with local planning department before signing lease.
                  </p>
                </div>

                <div className="border-l-4 border-orange-500 pl-4">
                  <h4 className="font-semibold mb-1">
                    Health & Safety Permits
                  </h4>
                  <p className="text-sm text-gray-600">
                    Required for food service, childcare, healthcare, salons, and
                    other regulated businesses. Health department inspections
                    mandatory.
                  </p>
                </div>

                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold mb-1">
                    Fire Department Permit
                  </h4>
                  <p className="text-sm text-gray-600">
                    Needed for businesses using hazardous materials, large
                    gatherings, or requiring special fire safety measures.
                  </p>
                </div>

                <div className="border-l-4 border-yellow-500 pl-4">
                  <h4 className="font-semibold mb-1">
                    Sign Permit
                  </h4>
                  <p className="text-sm text-gray-600">
                    Required before installing exterior business signage. Must
                    comply with local sign ordinances and lease restrictions.
                  </p>
                </div>
              </div>
            </div>

            {/* Insurance Requirements */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Insurance Requirements for Tenants
              </h3>
              <p className="text-gray-700 mb-3">
                Most commercial leases require tenants to carry specific
                insurance:
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    General Liability Insurance
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Protects against claims of bodily injury or property damage.
                  </p>
                  <p className="text-xs text-gray-500">
                    Typical requirement: $1-2 million per occurrence
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Property/Contents Insurance
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Covers your business property, equipment, and inventory.
                  </p>
                  <p className="text-xs text-gray-500">
                    Landlord's insurance only covers building structure
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Workers' Compensation
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Mandatory if you have employees. Covers work-related injuries.
                  </p>
                  <p className="text-xs text-gray-500">
                    Requirements vary by state/country
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Business Interruption
                  </h4>
                  <p className="text-sm text-gray-600 mb-2">
                    Covers lost income if you can't operate due to covered
                    events.
                  </p>
                  <p className="text-xs text-gray-500">
                    Highly recommended, not always required
                  </p>
                </div>
              </div>

              <div className="mt-4 bg-blue-50 rounded-lg p-4">
                <p className="text-sm text-gray-700">
                  <strong>Important:</strong> Your lease will likely require the
                  landlord be named as "Additional Insured" on your liability
                  policy and that you provide a Certificate of Insurance (COI)
                  before moving in.
                </p>
              </div>
            </div>

            {/* Tenant Rights */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Understanding Your Rights as a Tenant
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  <strong>Quiet Enjoyment:</strong> Right to use the space
                  without unreasonable interference from landlord
                </li>
                <li>
                  <strong>Habitable Conditions:</strong> Landlord must maintain
                  safe, functional space (working utilities, no health hazards)
                </li>
                <li>
                  <strong>Privacy:</strong> Landlord must provide reasonable
                  notice (typically 24-48 hours) before entering
                </li>
                <li>
                  <strong>Security Deposit Return:</strong> Right to itemized
                  deductions and timely return (laws vary by location)
                </li>
                <li>
                  <strong>Non-Discrimination:</strong> Protected from
                  discrimination based on protected characteristics
                </li>
                <li>
                  <strong>Lease Enforcement:</strong> Right to hold landlord to
                  lease terms and seek legal remedy for violations
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* For Hosts */}
        <section className="bg-white rounded-2xl p-8 shadow-sm mb-8">
          <h2 className="text-3xl font-bold mb-6">For Hosts</h2>

          <div className="space-y-8">
            {/* Legal Requirements for Hosts */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Legal Requirements for Commercial Landlords
              </h3>
              <p className="text-gray-700 mb-3">
                Before listing your space, ensure you're legally able to rent it:
              </p>

              <div className="space-y-3">
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Verify Commercial Zoning
                  </h4>
                  <p className="text-sm text-gray-700">
                    Confirm your property is zoned for commercial use and the
                    specific business types you'll allow. Contact local planning
                    department for verification.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Building Code Compliance
                  </h4>
                  <p className="text-sm text-gray-700">
                    Ensure property meets all commercial building codes,
                    including fire safety, accessibility (ADA in US), electrical,
                    and structural requirements.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Certificate of Occupancy
                  </h4>
                  <p className="text-sm text-gray-700">
                    Obtain or update CO for commercial use. Specify permitted
                    business types. Must be provided to tenants.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">
                    Business License
                  </h4>
                  <p className="text-sm text-gray-700">
                    Some jurisdictions require landlords to have a rental
                    property business license. Check local requirements.
                  </p>
                </div>
              </div>
            </div>

            {/* Landlord Insurance */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Insurance Requirements for Hosts
              </h3>
              <p className="text-gray-700 mb-3">
                Protect your investment with appropriate coverage:
              </p>

              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  <strong>Commercial Property Insurance:</strong> Covers
                  building damage from fire, weather, vandalism, etc.
                </li>
                <li>
                  <strong>Liability Insurance:</strong> Protects against injury
                  or damage claims from tenants or visitors
                </li>
                <li>
                  <strong>Loss of Rent Coverage:</strong> Compensates for lost
                  rental income if property becomes uninhabitable
                </li>
                <li>
                  <strong>Umbrella Policy:</strong> Additional liability
                  coverage beyond standard policy limits
                </li>
                <li>
                  <strong>Flood Insurance:</strong> If in flood-prone area
                  (usually not covered by standard policies)
                </li>
              </ul>

              <div className="mt-4 bg-orange-50 border-l-4 border-orange-500 p-4">
                <p className="text-sm text-gray-700">
                  <strong>Critical:</strong> Require all tenants to carry their
                  own insurance and name you as Additional Insured. Verify
                  coverage annually.
                </p>
              </div>
            </div>

            {/* Lease Best Practices */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Lease Agreement Best Practices
              </h3>
              <p className="text-gray-700 mb-3">
                Essential elements to include in your commercial lease:
              </p>

              <div className="space-y-2">
                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <h4 className="font-semibold text-sm">
                    Clear Use Restrictions
                  </h4>
                  <p className="text-sm text-gray-600">
                    Specify exactly what business types are permitted and any
                    prohibited activities.
                  </p>
                </div>

                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <h4 className="font-semibold text-sm">
                    Maintenance Responsibilities
                  </h4>
                  <p className="text-sm text-gray-600">
                    Clearly define what landlord maintains vs. tenant
                    obligations (HVAC, plumbing, structural, etc.)
                  </p>
                </div>

                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <h4 className="font-semibold text-sm">
                    Insurance Requirements
                  </h4>
                  <p className="text-sm text-gray-600">
                    Specify minimum coverage amounts, Additional Insured
                    requirements, and COI delivery timeline.
                  </p>
                </div>

                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <h4 className="font-semibold text-sm">
                    Default & Remedies
                  </h4>
                  <p className="text-sm text-gray-600">
                    Define what constitutes default, cure periods, and your
                    remedies (eviction process, damages, etc.)
                  </p>
                </div>

                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <h4 className="font-semibold text-sm">
                    Alterations & Improvements
                  </h4>
                  <p className="text-sm text-gray-600">
                    Specify approval process for tenant improvements and who owns
                    them at lease end.
                  </p>
                </div>

                <div className="border-l-4 border-green-500 pl-4 py-2">
                  <h4 className="font-semibold text-sm">
                    Assignment & Subletting
                  </h4>
                  <p className="text-sm text-gray-600">
                    Control whether tenant can transfer lease or sublet, and
                    under what conditions.
                  </p>
                </div>
              </div>

              <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-500 p-4">
                <p className="text-sm text-gray-700">
                  <strong>Legal Review Required:</strong> Always have a
                  commercial real estate attorney draft or review your lease
                  agreement. Generic templates may not protect your interests or
                  comply with local laws.
                </p>
              </div>
            </div>

            {/* Landlord Obligations */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Your Obligations as a Landlord
              </h3>
              <ul className="list-disc list-inside text-gray-600 space-y-2 ml-4">
                <li>
                  Maintain property in safe, habitable condition compliant with
                  building codes
                </li>
                <li>
                  Make necessary repairs to structural elements, roof, and major
                  systems
                </li>
                <li>
                  Respect tenant's right to quiet enjoyment and privacy
                </li>
                <li>
                  Provide proper notice before entering leased premises (except
                  emergencies)
                </li>
                <li>
                  Return security deposits within legally required timeframes
                  with itemized deductions
                </li>
                <li>
                  Comply with fair housing laws and non-discrimination
                  requirements
                </li>
                <li>
                  Maintain common areas and provide agreed-upon services
                </li>
                <li>
                  Follow proper legal procedures for eviction if necessary
                </li>
              </ul>
            </div>

            {/* Tax Considerations */}
            <div>
              <h3 className="text-2xl font-semibold mb-4">
                Tax Considerations for Hosts
              </h3>
              <p className="text-gray-700 mb-3">
                Rental income has tax implications you should understand:
              </p>

              <div className="space-y-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Rental Income</h4>
                  <p className="text-sm text-gray-600">
                    All rental payments are taxable income. Must be reported to
                    tax authorities. Keep detailed records of all income
                    received.
                  </p>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Deductible Expenses</h4>
                  <p className="text-sm text-gray-600 mb-2">
                    You can typically deduct:
                  </p>
                  <ul className="text-xs text-gray-500 list-disc list-inside ml-2">
                    <li>Mortgage interest and property taxes</li>
                    <li>Repairs and maintenance</li>
                    <li>Insurance premiums</li>
                    <li>Professional fees (legal, accounting)</li>
                    <li>Utilities (if you pay them)</li>
                    <li>Depreciation of the building</li>
                  </ul>
                </div>

                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2">Depreciation</h4>
                  <p className="text-sm text-gray-600">
                    Commercial property can be depreciated over 39 years (in US).
                    Significant tax benefit but requires recapture when you sell.
                  </p>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <p className="text-sm text-gray-700">
                    <strong>Consult a Tax Professional:</strong> Tax laws are
                    complex and vary by location. Work with a CPA or tax advisor
                    familiar with commercial real estate.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Dispute Resolution */}
        <section className="bg-white rounded-2xl p-8 shadow-sm">
          <h2 className="text-3xl font-bold mb-6">Dispute Resolution</h2>

          <div className="space-y-6">
            <p className="text-gray-700">
              When conflicts arise between landlords and tenants, there are
              several paths to resolution:
            </p>

            <div className="space-y-4">
              <div className="border-l-4 border-blue-500 pl-4">
                <h3 className="font-semibold text-lg mb-2">
                  1. Direct Communication
                </h3>
                <p className="text-gray-600 text-sm">
                  Most issues can be resolved through open, documented
                  communication. Always communicate in writing (email) to create
                  a record.
                </p>
              </div>

              <div className="border-l-4 border-green-500 pl-4">
                <h3 className="font-semibold text-lg mb-2">2. Mediation</h3>
                <p className="text-gray-600 text-sm">
                  Neutral third-party mediator helps both sides reach agreement.
                  Faster and less expensive than court. Non-binding unless
                  agreement is reached.
                </p>
              </div>

              <div className="border-l-4 border-orange-500 pl-4">
                <h3 className="font-semibold text-lg mb-2">3. Arbitration</h3>
                <p className="text-gray-600 text-sm">
                  Arbitrator hears both sides and makes binding decision. Often
                  required by lease agreements. More formal than mediation but
                  less than court.
                </p>
              </div>

              <div className="border-l-4 border-red-500 pl-4">
                <h3 className="font-semibold text-lg mb-2">4. Legal Action</h3>
                <p className="text-gray-600 text-sm">
                  Filing a lawsuit in court. Most expensive and time-consuming
                  option. Should be last resort after other methods fail.
                  Consult attorney before proceeding.
                </p>
              </div>
            </div>

            <div className="bg-blue-50 rounded-lg p-6 mt-6">
              <h3 className="font-semibold mb-3">
                Prevention is the Best Strategy
              </h3>
              <ul className="text-sm text-gray-700 space-y-2">
                <li>
                  • Use clear, comprehensive lease agreements reviewed by
                  attorneys
                </li>
                <li>• Document everything in writing</li>
                <li>• Communicate regularly and professionally</li>
                <li>• Address issues promptly before they escalate</li>
                <li>• Maintain good records of payments, repairs, and notices</li>
                <li>• Follow all legal requirements for your jurisdiction</li>
              </ul>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
